import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadGameData, getLearnset as fetchLearnset, getLearnsetHistory as fetchLearnsetHistory } from '../hooks/useGameData';
import type {
  GameMove,
  GameAbility,
  GameItem,
  GameNature,
  LearnsetData,
  LearnsetMoveEntry,
  LearnsetLevelEntry,
  LearnsetHistory,
} from '../hooks/useGameData';

// This context is a thin adapter around the useGameData singleton (see
// hooks/useGameData.ts) — there is only one actual fetch/cache implementation;
// this just re-shapes it into the Record-keyed-by-name form this context's
// consumers (Pokedex/PokemonPage/SetsPage) already expect.

export type MoveData = GameMove;
export type AbilityData = GameAbility;
export type ItemData = GameItem;
export type NatureData = GameNature;
export type { LearnsetData, LearnsetMoveEntry, LearnsetLevelEntry, LearnsetHistory };

export type GameDataStatus = 'loading' | 'ready' | 'error';

interface GameDataContextType {
  /** All moves keyed by name (e.g. "Thunderbolt") */
  moves: Record<string, MoveData>;
  /** All abilities keyed by name (e.g. "Adaptability") */
  abilities: Record<string, AbilityData>;
  /** All items keyed by name (e.g. "Choice Band") */
  items: Record<string, ItemData>;
  /** All natures as an array */
  natures: NatureData[];
  status: GameDataStatus;
  /** Fetch learnset for a pokemon slug; result is cached in-memory */
  getLearnset: (slug: string) => Promise<LearnsetData | null>;
  /** Fetch the admin-generated per-generation reference learnset (Gen I-IX) for a pokemon slug */
  getLearnsetHistory: (slug: string) => Promise<LearnsetHistory | null>;
}

const GameDataContext = createContext<GameDataContextType | undefined>(undefined);

export const useGameData = (): GameDataContextType => {
  const ctx = useContext(GameDataContext);
  if (!ctx) throw new Error('useGameData must be used within <GameDataProvider>');
  return ctx;
};

export const GameDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [moves,     setMoves]     = useState<Record<string, MoveData>>({});
  const [abilities, setAbilities] = useState<Record<string, AbilityData>>({});
  const [items,     setItems]     = useState<Record<string, ItemData>>({});
  const [natures,   setNatures]   = useState<NatureData[]>([]);
  const [status,    setStatus]    = useState<GameDataStatus>('loading');

  useEffect(() => {
    let cancelled = false;

    loadGameData()
      .then(data => {
        if (cancelled) return;
        setMoves(Object.fromEntries(data.movesByName));
        setAbilities(Object.fromEntries(data.abilitiesByName));
        setItems(Object.fromEntries(data.itemsByName));
        setNatures(data.natures);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <GameDataContext.Provider value={{ moves, abilities, items, natures, status, getLearnset: fetchLearnset, getLearnsetHistory: fetchLearnsetHistory }}>
      {children}
    </GameDataContext.Provider>
  );
};
